import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Link2, Paperclip, SendHorizontal, StickyNote, X } from 'lucide-react'
import type { Agent } from '@/api/directoryApi'
import { errorMessage } from '@/components/common/States'
import { RecordLinkDialog } from '@/components/ticket/RecordLinkDialog'
import { Button } from '@/components/ui/button'
import { useAgents } from '@/hooks/useDirectory'
import { usePostMessage } from '@/hooks/useMessages'
import { useUploadAttachment } from '@/hooks/useAttachments'
import { formatBytes } from '@/lib/threadView'
import { cn } from '@/lib/utils'
import { useToast } from '@/providers/ToastProvider'

export type ComposerMode = 'reply' | 'note'

interface ComposerProps {
  routeId: string
  /** Whose reply this is: an agent's `agent_reply` or a customer's `customer_reply`. */
  author: 'agent' | 'customer'
  canReply: boolean
  canNote: boolean
  canAttach: boolean
  canLink: boolean
  /** First name of the customer being answered ("Reply to Kwame…"). */
  replyTo: string | null
  initialMode?: ComposerMode
  autoFocus?: boolean
  showHint?: boolean
  onSent?: () => void
}

/** `@` + word just before the caret, or null. */
function mentionQuery(text: string, caret: number): { start: number; query: string } | null {
  const before = text.slice(0, caret)
  const m = /(^|\s)@([\p{L}.'-]{0,30})$/u.exec(before)
  if (!m) return null
  return { start: caret - m[2].length - 1, query: m[2].toLowerCase() }
}

/**
 * Reply / internal-note composer (artboard 02). Attachments upload after the
 * message is stored (presign → PUT → confirm with its messageId) so each file
 * carries the message's visibility.
 */
export function Composer({
  routeId,
  author,
  canReply,
  canNote,
  canAttach,
  canLink,
  replyTo,
  initialMode,
  autoFocus,
  showHint = true,
  onSent,
}: ComposerProps) {
  const { t } = useTranslation()
  const { notify } = useToast()
  const [mode, setMode] = useState<ComposerMode>(initialMode ?? (canReply ? 'reply' : 'note'))
  const [body, setBody] = useState('')
  const [files, setFiles] = useState<File[]>([])
  const [mentions, setMentions] = useState<Agent[]>([])
  const [picker, setPicker] = useState<{ start: number; query: string } | null>(null)
  const [pickerIndex, setPickerIndex] = useState(0)
  const [linkOpen, setLinkOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const textRef = useRef<HTMLTextAreaElement>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  const post = usePostMessage(routeId)
  const upload = useUploadAttachment(routeId)
  const agents = useAgents(author === 'agent')

  useEffect(() => {
    if (autoFocus) textRef.current?.focus()
  }, [autoFocus])

  const matches = useMemo(() => {
    if (!picker || !agents.data) return []
    return agents.data.filter((a) => a.name.toLowerCase().includes(picker.query) || a.email.toLowerCase().startsWith(picker.query)).slice(0, 6)
  }, [picker, agents.data])

  const isNote = mode === 'note'
  const busy = post.isPending || upload.isPending
  const placeholder = isNote
    ? t('composer.notePlaceholder')
    : author === 'customer'
      ? t('composer.customerPlaceholder')
      : replyTo
        ? t('composer.replyPlaceholder', { name: replyTo })
        : t('composer.replyPlaceholderGeneric')

  function onChange(value: string, caret: number) {
    setBody(value)
    setError(null)
    setPicker(author === 'agent' ? mentionQuery(value, caret) : null)
    setPickerIndex(0)
  }

  function pickMention(agent: Agent) {
    if (!picker) return
    const caret = picker.start + 1 + picker.query.length
    const next = `${body.slice(0, picker.start)}@${agent.name} ${body.slice(caret)}`
    setBody(next)
    setMentions((m) => (m.some((x) => x.id === agent.id) ? m : [...m, agent]))
    setPicker(null)
    requestAnimationFrame(() => {
      const pos = picker.start + agent.name.length + 2
      textRef.current?.focus()
      textRef.current?.setSelectionRange(pos, pos)
    })
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (picker && matches.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setPickerIndex((i) => (i + 1) % matches.length)
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setPickerIndex((i) => (i - 1 + matches.length) % matches.length)
        return
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault()
        pickMention(matches[pickerIndex])
        return
      }
      if (e.key === 'Escape') {
        setPicker(null)
        return
      }
    }
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault()
      void send()
    }
  }

  function insertText(text: string) {
    const el = textRef.current
    const at = el ? el.selectionStart : body.length
    const sep = at > 0 && !/\s$/.test(body.slice(0, at)) ? ' ' : ''
    setBody(`${body.slice(0, at)}${sep}${text}${body.slice(at)}`)
  }

  async function send() {
    const text = body.trim()
    if (!text || busy) return
    setError(null)
    // Only keep mentions whose @Name is still in the text.
    const mentionIds = mentions.filter((a) => text.includes(`@${a.name}`)).map((a) => a.id)
    try {
      const kind = isNote ? 'internal_note' : author === 'customer' ? 'customer_reply' : 'agent_reply'
      const res = await post.mutateAsync({ kind, body: text, mentions: mentionIds.length > 0 ? mentionIds : undefined })
      // The message is stored: clear it so a failed upload can't cause a duplicate resend.
      setBody('')
      setMentions([])
      for (const file of files) {
        await upload.mutateAsync({ file, visibility: isNote ? 'internal' : 'customer', messageId: res.message.id })
        setFiles((all) => all.filter((f) => f !== file))
      }
      if (res.followUp) notify({ title: t('composer.followUp', { key: res.followUp.key }), href: `/tickets/${res.followUp.key}` })
      else if (res.reopened) notify({ title: t('composer.reopened') })
      onSent?.()
    } catch (err) {
      setError(t('composer.sendError', { message: errorMessage(err) }))
    }
  }

  const toolButton =
    'flex items-center gap-1.5 whitespace-nowrap rounded-md px-2 py-1 text-meta text-ink-muted hover:bg-surface-subtle hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 [&_svg]:size-3.5'

  return (
    <div
      className={cn(
        'relative flex flex-col rounded-lg border bg-background',
        // The textarea drops its own outline, so the box shows focus.
        isNote ? 'border-note-border bg-note-bg/40 focus-within:border-note-heading' : 'border-line-strong focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/15',
      )}
    >
      {isNote ? (
        <p className="px-4 pt-3 text-eyebrow font-semibold uppercase text-note-heading">{t('composer.noteMode')}</p>
      ) : null}
      <textarea
        ref={textRef}
        value={body}
        aria-label={placeholder}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value, e.target.selectionStart)}
        onKeyDown={onKeyDown}
        onBlur={() => window.setTimeout(() => setPicker(null), 150)}
        className="h-[72px] shrink-0 resize-none bg-transparent px-4 py-3.5 text-body text-ink placeholder:text-ink-placeholder focus-visible:outline-none"
      />

      {picker && author === 'agent' ? (
        <ul
          role="listbox"
          aria-label={t('composer.mentionList')}
          className="absolute bottom-full left-4 z-20 mb-1 w-72 rounded-md border border-line-strong bg-background p-1 shadow-lg"
        >
          {matches.length === 0 ? (
            <li className="px-2 py-1.5 text-meta text-ink-muted">{t('composer.mentionNoMatch')}</li>
          ) : (
            matches.map((a, i) => (
              <li key={a.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={i === pickerIndex}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pickMention(a)}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-meta',
                    i === pickerIndex ? 'bg-surface-subtle' : 'hover:bg-surface-subtle',
                  )}
                >
                  <span className="flex size-6 items-center justify-center rounded-full bg-primary text-[10px] font-semibold text-primary-foreground">{a.initials}</span>
                  <span className="text-ink">{a.name}</span>
                  <span className="truncate text-label text-ink-muted">{a.title ?? a.team ?? ''}</span>
                </button>
              </li>
            ))
          )}
        </ul>
      ) : null}

      {files.length > 0 ? (
        <div className="flex flex-wrap gap-2 px-4 pb-3">
          {files.map((f, i) => (
            <span key={`${f.name}-${i}`} className="flex items-center gap-2 rounded-md border border-line-strong px-2.5 py-1 text-label">
              <span className="text-ink-strong">{f.name}</span>
              <span className="text-ink-faint">{formatBytes(f.size)}</span>
              <button
                type="button"
                aria-label={t('composer.removeFile', { name: f.name })}
                onClick={() => setFiles((all) => all.filter((_, j) => j !== i))}
                className="text-ink-muted hover:text-ink"
              >
                <X className="size-3" />
              </button>
            </span>
          ))}
        </div>
      ) : null}

      {error ? (
        <p role="alert" className="px-4 pb-2 text-label text-status-escalated-fg">
          {error}
        </p>
      ) : null}

      <div className="flex items-center justify-between border-t border-line py-2.5 pl-4 pr-3">
        <div className="-ml-2 flex gap-1">
          {canAttach ? (
            <>
              <button type="button" className={toolButton} onClick={() => fileRef.current?.click()} disabled={busy}>
                <Paperclip aria-hidden="true" />
                {t('composer.attach')}
              </button>
              <input
                ref={fileRef}
                type="file"
                multiple
                hidden
                onChange={(e) => {
                  const picked = Array.from(e.target.files ?? [])
                  setFiles((all) => [...all, ...picked])
                  e.target.value = ''
                }}
              />
            </>
          ) : null}
          {canLink ? (
            <button type="button" className={toolButton} onClick={() => setLinkOpen(true)} disabled={busy}>
              <Link2 aria-hidden="true" />
              {t('composer.insertRecord')}
            </button>
          ) : null}
          {canNote && canReply ? (
            <button
              type="button"
              aria-pressed={isNote}
              className={cn(toolButton, isNote && 'bg-note-bg font-medium text-note-heading hover:bg-note-bg hover:text-note-heading')}
              onClick={() => setMode(isNote ? 'reply' : 'note')}
            >
              <StickyNote aria-hidden="true" />
              {t('composer.internalNote')}
            </button>
          ) : null}
        </div>
        <div className="flex min-w-0 items-center gap-3">
          {showHint && author === 'agent' ? <span className="hidden truncate text-label text-ink-muted xl:inline">{t('composer.hint')}</span> : null}
          <Button type="button" size="sm" onClick={() => void send()} disabled={busy || !body.trim()}>
            <SendHorizontal aria-hidden="true" />
            {busy ? t('composer.sending') : isNote ? t('composer.addNote') : t('composer.send')}
          </Button>
        </div>
      </div>

      {canLink ? <RecordLinkDialog routeId={routeId} open={linkOpen} onOpenChange={setLinkOpen} onInsertText={insertText} /> : null}
    </div>
  )
}
